import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFile from "@mui/icons-material/AttachFile";
import DateRangeSharp from "@mui/icons-material/DateRangeSharp";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { FileGroup } from "common/FileUpload";
import { FileProvider, useFileContext } from "context/FilesV2";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { enqueueSnackbar } from "notistack";
import { forwardRef, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  downloadFile as downloadStoredFile,
  getFiles,
} from "src/apis/controllers/files";
import { assignChildProtectionOfficer, deleteChildProtectionOfficer } from "src/apis/controllers/mhdProfile";
import {
  downloadFile as previewStoredFile,
  formatFiles,
  uploadFiles as uploadFileGroups,
} from "src/util";
import { formatDate } from "./ProfileComponents";
import MhdFilePreviewDialog from "./MhdFilePreviewDialog";

const getToday = () => format(new Date(), "yyyy-MM-dd");

const getValidDate = (value) => {
  if (!value) return null;

  const date = value instanceof Date
    ? value
    : typeof value === "string"
      ? parseISO(value)
      : null;

  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getDateValue = (value) => {
  const date = getValidDate(value);
  return date ? format(date, "yyyy-MM-dd") : "";
};

const GreekDateInput = forwardRef(
  ({ label, required, value, onClick, onChange }, ref) => (
    <TextField
      label={label}
      required={required}
      value={value || ""}
      onClick={onClick}
      onChange={onChange}
      inputRef={ref}
      placeholder="ηη/μμ/εεεε"
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label={`Επιλογή ημερομηνίας ${label}`} onClick={onClick} edge="end">
              <DateRangeSharp />
            </IconButton>
          </InputAdornment>
        ),
      }}
      fullWidth
    />
  ),
);

GreekDateInput.displayName = "GreekDateInput";

const getAssignmentErrorMessage = (error) => {
  if (typeof error === "string") return error;
  return error?.error || error?.message || "Δεν ολοκληρώθηκε ο ορισμός.";
};

const OfficerDialogContent = ({ open, onClose, applicationId, employee, onSaved }) => {
  const [dateFrom, setDateFrom] = useState(getToday);
  const [dateTo, setDateTo] = useState("");
  const [fileConfigLoading, setFileConfigLoading] = useState(false);
  const [fileConfigError, setFileConfigError] = useState("");
  const [saving, setSaving] = useState(false);
  const { fileGroups, control: filesControl } = useFileContext();
  const decisionBundle = fileGroups
    .flatMap((group) => group.bundles ?? [])
    .find((bundle) => bundle.files?.length > 0);

  useEffect(() => {
    if (!open) return;

    setDateFrom(getToday());
    setDateTo("");
    setFileConfigError("");

    let isActive = true;
    const loadFileConfig = async () => {
      setFileConfigLoading(true);
      try {
        const response = await getFiles({
          DataKey: "MHD_ChildProtectionOfficer",
          DataValue: null,
          RecordId: null,
          RecordGuid: null,
          ProjectTableId: 22,
        });
        if (!isActive) return;

        const configuredGroups = formatFiles(response?.data ?? []);
        if (configuredGroups.length === 0) {
          filesControl.initialize([]);
          setFileConfigError("Δεν βρέθηκε η παραμετροποίηση του υποχρεωτικού αρχείου.");
          return;
        }

        filesControl.initialize(configuredGroups);
        filesControl.setIsDirty?.(false);
      } catch {
        if (isActive) {
          filesControl.initialize([]);
          setFileConfigError("Δεν φορτώθηκε η παραμετροποίηση του υποχρεωτικού αρχείου.");
        }
      } finally {
        if (isActive) setFileConfigLoading(false);
      }
    };

    loadFileConfig();
    return () => {
      isActive = false;
    };
  }, [open, employee?.id]);

  const handleSave = async () => {
    if (!employee?.id || !dateFrom || !decisionBundle) return;
    setSaving(true);
    try {
      if (filesControl.isDirty) {
        const uploadResponse = await filesControl.uploadFileGroups();
        if (uploadResponse?.status !== "success") {
          throw new Error("Δεν ολοκληρώθηκε η μεταφόρτωση της απόφασης ορισμού.");
        }
        filesControl.setIsDirty?.(false);
      }

      const response = await assignChildProtectionOfficer({
        applicationId,
        employeeId: employee.id,
        dateFrom,
        dateTo: dateTo || null,
        decisionBundleId: decisionBundle.id,
      });
      if (response?.succeeded === false) {
        enqueueSnackbar(response?.message || "Δεν ολοκληρώθηκε ο ορισμός.", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(response?.message || "Ο ορισμός ολοκληρώθηκε.", {
        variant: response?.status === "warning" ? "warning" : "success",
      });
      onSaved();
      onClose();
    } catch (error) {
      enqueueSnackbar(getAssignmentErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Νέος Υπεύθυνος Προστασίας Ανηλίκων</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity={employee?.hasPayrollExpenses ? "info" : "warning"}>
            Υπάλληλος: <strong>{employee?.lastName} {employee?.firstName}</strong>
            {employee?.jobTitle ? ` · ${employee.jobTitle}` : ""}
            {!employee?.hasPayrollExpenses
              ? " · Δεν υπάρχουν μισθολογικές δαπάνες για τον υπάλληλο."
              : ""}
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DatePicker
              selected={getValidDate(dateFrom)}
              onChange={(date) => setDateFrom(getDateValue(date))}
              dateFormat="dd/MM/yyyy"
              locale={el}
              customInput={<GreekDateInput label="Από" required />}
              wrapperClassName="mhd-profile-date-picker"
              portalId="mhd-profile-datepicker-portal"
              popperClassName="mhd-profile-datepicker-popper"
              showYearDropdown
              dropdownMode="select"
            />
            <DatePicker
              selected={getValidDate(dateTo)}
              onChange={(date) => setDateTo(getDateValue(date))}
              dateFormat="dd/MM/yyyy"
              locale={el}
              customInput={<GreekDateInput label="Έως" />}
              wrapperClassName="mhd-profile-date-picker"
              portalId="mhd-profile-datepicker-portal"
              popperClassName="mhd-profile-datepicker-popper"
              isClearable
              showYearDropdown
              dropdownMode="select"
            />
          </Stack>
          <Alert severity="info">
            Με τον νέο ορισμό κλείνει αυτόματα ο προηγούμενος ενεργός υπεύθυνος.
          </Alert>
          {fileConfigLoading && <Typography variant="body2">Φόρτωση αρχείου...</Typography>}
          {!fileConfigLoading && fileGroups.map((group) => (
            <Box key={group.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                Υποχρεωτικό αρχείο
              </Typography>
              <FileGroup
                reviewMode={false}
                isReadOnly={saving}
                canAdd
                canDelete
                id={group.id}
                title={group.title}
                maxSize={2097152}
                maxFiles={1}
                maxBundles={group.maxBundles}
                fields={group.fields}
                bundles={group.bundles}
                templates={group.templates}
                extraTypeDataKeys={group.extraTypeDataKeys}
              />
            </Box>
          ))}
          {fileConfigError && <Alert severity="error">{fileConfigError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Ακύρωση</Button>
        <Button
          variant="contained"
          disabled={saving || fileConfigLoading || !employee?.id || !dateFrom || !decisionBundle}
          onClick={handleSave}
          sx={{ textTransform: "none" }}
        >
          Αποθήκευση
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OfficerDialog = (props) => (
  <FileProvider
    downloadFile={(fileId, type) => previewStoredFile(fileId, type)}
    uploadFiles={(groups) => uploadFileGroups(groups, null)}
  >
    <OfficerDialogContent {...props} />
  </FileProvider>
);

const formatPeriod = (period) =>
  `${formatDate(period?.dateFrom)} – ${period?.dateTo ? formatDate(period.dateTo) : "σήμερα"}`;

const rowHeightFor = (item) => {
  const periodCount = Math.max(
    item?.payrollPeriods?.length || 0,
    item?.assignments?.length || 0,
    item?.cvFiles?.length || 0,
    1,
  );
  return Math.max(40, 12 + periodCount * 26);
};

const DeleteAssignmentDialog = ({ assignment, deleting, onClose, onConfirm }) => (
  <Dialog open={Boolean(assignment)} onClose={deleting ? undefined : onClose} fullWidth maxWidth="xs">
    <DialogTitle>Αφαίρεση ορισμού ΥΠΑ</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        Θέλετε να αφαιρέσετε τον ορισμό {assignment ? formatPeriod(assignment) : ""};
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={deleting}>Ακύρωση</Button>
      <Button color="error" variant="contained" onClick={onConfirm} disabled={deleting}>
        Αφαίρεση
      </Button>
    </DialogActions>
  </Dialog>
);

const StaffSection = ({ profile, onRefresh }) => {
  const [filter, setFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [openingFileId, setOpeningFileId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const employees = profile?.employees ?? [];
  const officers = profile?.childProtectionOfficers ?? [];
  const filteredEmployees = useMemo(
    () =>
      employees.filter((employee) => {
        if (filter === "payroll") return employee.hasPayrollExpenses;
        if (filter === "missing") return !employee.hasPayrollExpenses;
        return true;
      }),
    [employees, filter],
  );
  const rows = useMemo(() => filteredEmployees.map((employee) => ({
    ...employee,
    fullName: `${employee.lastName} ${employee.firstName}`,
    payrollPeriods: employee.payrollPeriods || [],
    payrollPeriod: (employee.payrollPeriods || []).map(formatPeriod).join("\n"),
    source: employee.isFromExpenses ? "Δαπάνες" : "Αίτηση",
    assignments: officers
      .filter((officer) => officer.employeeId === employee.id)
      .sort((left, right) => left.dateFrom.localeCompare(right.dateFrom)),
  })), [filteredEmployees, officers]);

  const handleOpenFile = async (file) => {
    if (!file?.id) return;

    setOpeningFileId(file.id);
    try {
      const data = await downloadStoredFile({ Id: file.id }, { responseType: "blob" });
      const contentType = file.contentType || data?.type || "application/octet-stream";
      const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
      setFileToPreview({ blob, name: file.fileName || "Αρχείο", contentType });
    } catch {
      enqueueSnackbar("Δεν ήταν δυνατή η προεπισκόπηση του αρχείου.", { variant: "error" });
    } finally {
      setOpeningFileId(null);
    }
  };

  const handleDeleteAssignment = async () => {
    if (!assignmentToDelete?.id || !profile?.applicationId) return;

    setDeleting(true);
    try {
      const response = await deleteChildProtectionOfficer(profile.applicationId, assignmentToDelete.id);
      if (response?.succeeded === false) {
        enqueueSnackbar(response?.message || "Δεν ολοκληρώθηκε η αφαίρεση.", { variant: "error" });
        return;
      }

      enqueueSnackbar(response?.message || "Ο ορισμός αφαιρέθηκε.", { variant: "success" });
      setAssignmentToDelete(null);
      await onRefresh();
    } catch (error) {
      enqueueSnackbar(getAssignmentErrorMessage(error), { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box component="section">
      {fileToPreview && (
        <MhdFilePreviewDialog file={fileToPreview} onClose={() => setFileToPreview(null)} />
      )}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography component="h2" variant="h4" fontWeight={700}>Προσωπικό ΜΗΔ</Typography>
          <Chip size="small" label={employees.length} />
        </Stack>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="staff-filter-label">Προσωπικό</InputLabel>
          <Select
            labelId="staff-filter-label"
            value={filter}
            label="Προσωπικό"
            onChange={(event) => setFilter(event.target.value)}
          >
            <MenuItem value="all">Όλοι</MenuItem>
            <MenuItem value="payroll">Με μισθολογικές δαπάνες</MenuItem>
            <MenuItem value="missing">Χωρίς μισθολογικές δαπάνες</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {rows.length ? (
        <FlexGrid
          className="mhd-profile-grid"
          itemsSource={rows}
          isReadOnly
          autoClipboard
          selectionMode="CellRange"
          headersVisibility="All"
          alternatingRowStep={0}
          style={{ height: Math.min(460, 52 + rows.reduce((height, item) => height + rowHeightFor(item), 0)) }}
          initialized={(grid) => {
            grid.rows.defaultSize = 40;
            grid.columnHeaders.rows.defaultSize = 34;
            grid.rowHeaders.columns.defaultSize = 28;
          }}
          loadedRows={(grid) => {
            grid.rows.forEach((row) => {
              row.height = rowHeightFor(row.dataItem);
            });
          }}
        >
          <FlexGridFilter />
          <FlexGridColumn binding="fullName" header="Ονοματεπώνυμο" width="2*" minWidth={185} />
          <FlexGridColumn binding="jobTitle" header="Ειδικότητα" width="2*" minWidth={160} />
          <FlexGridColumn binding="amka" header="ΑΜΚΑ" width={140} />
          <FlexGridColumn binding="afm" header="ΑΦΜ" width={115} />
          <FlexGridColumn header="Βιογραφικό" width={145}>
            <FlexGridCellTemplate cellType="Cell" template={({ item }) => item.cvFiles?.length ? (
              <Stack spacing={0.25} alignItems="flex-start">
                {item.cvFiles.map((file, index) => (
                  <Button
                    key={file.id}
                    size="small"
                    variant="text"
                    startIcon={<AttachFile fontSize="small" />}
                    disabled={openingFileId === file.id}
                    title={file.fileName}
                    onClick={() => handleOpenFile(file)}
                    sx={{ minWidth: 0, p: 0.25, textTransform: "none" }}
                  >
                    {item.cvFiles.length > 1 ? `CV ${index + 1}` : "CV"}
                  </Button>
                ))}
              </Stack>
            ) : "-"} />
          </FlexGridColumn>
          <FlexGridColumn binding="payrollPeriod" header="Περίοδος μισθοδοσίας" width={225}>
            <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
              <Box sx={{ lineHeight: 1.15 }}>
                {item.payrollPeriods.map((period) => (
                  <Typography key={`${period.dateFrom}-${period.dateTo || "today"}`} variant="body2">
                    {formatPeriod(period)}
                  </Typography>
                ))}
              </Box>
            )} />
          </FlexGridColumn>
          <FlexGridColumn binding="source" header="Πηγή" width={90} />
          <FlexGridColumn header="Ορισμοί ΥΠΑ" width={260}>
            <FlexGridCellTemplate cellType="Cell" template={({ item }) => item.assignments.length ? (
              <Stack spacing={0.25}>
                {item.assignments.map((assignment) => (
                  <Stack key={assignment.id} direction="row" alignItems="center" spacing={0.25}>
                    {assignment.isActive ? (
                      <Stack direction="row" alignItems="center" spacing={0.5} title={assignment.warning || "Τρέχων ΥΠΑ"}>
                        <Chip size="small" color="success" label="Τρέχων" />
                        <Typography variant="body2" color="success.dark">
                          {formatPeriod(assignment)}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography
                        variant="body2"
                        color={assignment.hasPayrollCoverage ? "text.primary" : "warning.dark"}
                        title={assignment.warning || ""}
                      >
                        {formatPeriod(assignment)}
                      </Typography>
                    )}
                    {assignment.decisionFiles?.map((file) => (
                      <IconButton
                        key={file.id}
                        size="small"
                        color="primary"
                        sx={{ p: 0.25 }}
                        disabled={openingFileId === file.id}
                        title={file.fileName}
                        aria-label="Προβολή απόφασης ορισμού"
                        onClick={() => handleOpenFile(file)}
                      >
                        <AttachFile fontSize="inherit" />
                      </IconButton>
                    ))}
                    {profile?.canManageChildProtectionOfficer && (
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ p: 0.25 }}
                        aria-label={`Αφαίρεση ορισμού ${formatPeriod(assignment)}`}
                        onClick={() => setAssignmentToDelete(assignment)}
                      >
                        <DeleteOutline fontSize="inherit" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            ) : "-"} />
          </FlexGridColumn>
          {profile?.canManageChildProtectionOfficer && (
            <FlexGridColumn header="Ενέργεια" width={175}>
              <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
                <Button
                  size="small"
                  variant={item.assignments.some((assignment) => assignment.isActive) ? "outlined" : "contained"}
                  onClick={() => setSelectedEmployee(item)}
                  sx={{ textTransform: "none" }}
                >
                  {item.assignments.some((assignment) => assignment.isActive) ? "Νεος ορισμος" : "Ορισμος ΥΠΑ"}
                </Button>
              )} />
            </FlexGridColumn>
          )}
        </FlexGrid>
      ) : <Alert severity="info">Δεν υπάρχουν εγγραφές για το επιλεγμένο φίλτρο.</Alert>}

      <OfficerDialog
        key={selectedEmployee?.id ?? "closed"}
        open={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
        applicationId={profile.applicationId}
        employee={selectedEmployee}
        onSaved={onRefresh}
      />

      <DeleteAssignmentDialog
        assignment={assignmentToDelete}
        deleting={deleting}
        onClose={() => setAssignmentToDelete(null)}
        onConfirm={handleDeleteAssignment}
      />
    </Box>
  );
};

export default StaffSection;
